import { SNACKBAR_ITEM } from '@e2e/constants/share-component';
import {
  Alert,
  CircularProgress,
  Collapse,
  LinearProgress,
  Slide,
  type AlertProps,
} from '@shared/components';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { CustomSnackbar, type CustomSnackbarProps } from '../custom-snackbar';

const COLLAPSE_TIMEOUT = 300;

interface SnackbarItemProps {
  message: string;
  variant?: AlertProps['severity'];
  loading?: boolean;
  snack: Partial<CustomSnackbarProps>;
}

const SnackbarItem: React.FC<SnackbarItemProps> = (props) => {
  const { message, variant, loading = false, snack } = props;
  const timeout = useRef<NodeJS.Timeout | null>(null);
  const [collapsed, setCollapsed] = useState(true);
  const [snackbarState, setSnackbarState] = useState<'enter' | 'exit'>('enter');
  const {
    open = false,
    disableWindowBlurListener,
    autoHideDuration,
    onClose,
    ...otherSnack
  } = snack;
  const [progress, setProgress] = useState(100);
  const startTime = useRef<number | null>(null);

  const handleClose = useCallback(() => {
    if (snack.autoHideDuration) {
      setCollapsed(false);
    }
    setSnackbarState('exit');
  }, [snack]);

  const handleExitScreen = useCallback(() => {
    timeout.current = setTimeout(() => {
      setCollapsed(false);
      onClose?.(null, 'timeout', snack.id ?? '');
    }, COLLAPSE_TIMEOUT);
  }, []);

  const snackbarOpen = useMemo(() => {
    return snackbarState === 'enter' && snack.open;
  }, [snackbarState]);

  useEffect(() => {
    if (timeout.current) {
      clearTimeout(timeout.current);
    }
  }, []);

  useEffect(() => {
    if (snackbarOpen && autoHideDuration) {
      startTime.current = Date.now();
      const hideDuration = autoHideDuration - COLLAPSE_TIMEOUT;
      const interval = setInterval(() => {
        const elapsed = Date.now() - (startTime.current ?? 0);
        const newProgress = Math.max(0, 100 - (elapsed / hideDuration) * 100);
        setProgress(newProgress);

        if (newProgress <= 0) {
          clearInterval(interval);
        }
      }, 100);

      return () => {
        clearInterval(interval);
      };
    }
  }, [snackbarOpen, autoHideDuration]);

  return (
    <Collapse in={collapsed}>
      <CustomSnackbar
        open={open}
        id={otherSnack.id}
        autoHideDuration={autoHideDuration}
        disableWindowBlurListener={disableWindowBlurListener}
        onClose={handleClose}
        {...otherSnack}
      >
        <Slide appear in={snackbarOpen} mountOnEnter unmountOnExit onExit={handleExitScreen}>
          <Alert
            severity={variant}
            data-testid={SNACKBAR_ITEM}
            variant='outlined'
            icon={loading ? <CircularProgress size={20} color='inherit' /> : undefined}
            sx={{
              position: 'relative',
              overflow: 'hidden',
              minWidth: '300px',
            }}
          >
            {message}
            {autoHideDuration ? (
              <LinearProgress
                variant='determinate'
                color='inherit'
                value={progress}
                sx={(theme) => ({
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '6px',
                  borderRadius: theme.spacing(0),
                  color:
                    variant === 'muted'
                      ? theme.palette.grey[300]
                      : theme.palette[variant ?? 'info'].main,
                })}
              />
            ) : null}
          </Alert>
        </Slide>
      </CustomSnackbar>
    </Collapse>
  );
};
SnackbarItem.displayName = 'SnackbarItem';

export default SnackbarItem;
